import { SWRConfiguration } from 'swr';

import { useAllEmployees } from '~/data/employees';
import { useSelectedEmployeeId } from '~/hooks/employee';
import { UseArraySWROutput, useArraySWR } from '~/hooks/useSWR';
import { useVisible } from '~/hooks/useVisible';

import {
  EmplHistoryDetailType,
  getEmplDetailHistory,
} from './components/EmplDetailHistory/data';
import { EmplHistoryType, getEmplHistory } from './components/EmplHistory/data';
import {
  EmplOtherHistoryType,
  getEmplOtherHistory,
} from './components/EmplOtherHistory/data';

export type UseScheduleHistoryOutput = {
  emplHistory: EmplHistoryType[];
  emplDetailHistory: EmplHistoryDetailType[];
  emplOtherHistory: EmplOtherHistoryType[];
  isDialogOpen: boolean;
  openDialog: () => void;
  closeDialog: () => void;
};

const useGetScheduleEmploymentHistoryData = (
  props: {
    employeeId?: number;
  },
  swrConfig?: SWRConfiguration,
): UseArraySWROutput<EmplHistoryType> => {
  const { employeeId } = props;

  return useArraySWR<EmplHistoryType>(
    !!employeeId ? ['useGetScheduleEmploymentHistoryData', employeeId] : null,
    ([_key, employeeId]) => getEmplHistory(employeeId),
    swrConfig,
  );
};

const useGetScheduleEmploymentDetailHistoryData = (
  props: {
    employeeId?: number;
    employmentId?: number;
  },
  swrConfig?: SWRConfiguration,
): UseArraySWROutput<EmplHistoryDetailType> => {
  const { employeeId, employmentId } = props;

  return useArraySWR<EmplHistoryDetailType>(
    !!employeeId && !!employmentId
      ? ['useGetScheduleEmploymentDetailHistoryData', employeeId, employmentId]
      : null,
    ([_key, employeeId, employmentId]) =>
      getEmplDetailHistory(employeeId, employmentId),
    swrConfig,
  );
};

const useGetScheduleEmploymentOtherHistoryData = (
  props: {
    employeeId?: number;
  },
  swrConfig?: SWRConfiguration,
): UseArraySWROutput<EmplOtherHistoryType> => {
  const { employeeId } = props;

  return useArraySWR<EmplOtherHistoryType>(
    employeeId
      ? ['useGetScheduleEmploymentOtherHistoryData', employeeId]
      : null,
    ([_key, employeeId]) => getEmplOtherHistory(employeeId),
    swrConfig,
  );
};

export const useScheduleHistory = () => {
  const { selectedEmployeeId } = useSelectedEmployeeId();
  const empId = String(selectedEmployeeId);

  const { allEmployees } = useAllEmployees();
  const fEmpEmplId = allEmployees[parseInt(empId)];

  const swrConfig: SWRConfiguration = {
    revalidateOnFocus: false,
    revalidateIfStale: false,
  };

  const { data: emplHistory } = useGetScheduleEmploymentHistoryData(
    {
      employeeId: selectedEmployeeId,
    },
    swrConfig,
  );
  const { data: emplDetailHistory } = useGetScheduleEmploymentDetailHistoryData(
    {
      employeeId: selectedEmployeeId,
      employmentId: fEmpEmplId?.fEmpEmplId,
    },
    swrConfig,
  );
  const { data: emplOtherHistory } = useGetScheduleEmploymentOtherHistoryData(
    {
      employeeId: selectedEmployeeId,
    },
    swrConfig,
  );

  const {
    isVisible: isDialogOpen,
    show: openDialog,
    hide: closeDialog,
  } = useVisible();

  return {
    emplHistory,
    emplDetailHistory,
    emplOtherHistory,
    isDialogOpen,
    openDialog,
    closeDialog,
  };
};
